import React from "react";
import { Link } from "react-router-dom";
import { Handshake, ClipboardList, Wallet, FileText, MessageSquare, Upload, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";

const BENEFITS = [
  { icon: ClipboardList, title: "Steady Assigned Work", text: "Receive service jobs (stamping, attestation, processing and more) assigned directly to you from Gulfudaan's live application flow." },
  { icon: Wallet, title: "Transparent Payouts", text: "Track every job's payout, received amount and pending dues in your own portal — partial payouts recorded and confirmed on WhatsApp." },
  { icon: FileText, title: "Monthly Statements", text: "Download a PDF earnings statement for any month — jobs, payouts, received and pending, ready for your books." },
  { icon: Upload, title: "Publish Your Services", text: "Submit your own services for listing on Gulfudaan — approved services go live on the website and bring you customers." },
  { icon: MessageSquare, title: "Direct Admin Chat", text: "Built-in live chat and private job notes keep you and the Gulfudaan team in sync on every application." },
  { icon: Handshake, title: "Long-Term Partnership", text: "Grow with a platform that keeps sending you work — your performance, pricing and margins are managed transparently." },
];

const STEPS = [
  "Register with your company details and contact information",
  "Verify your email with the 6-digit OTP we send you",
  "Our team reviews and activates your partner account — then jobs start flowing",
];

export default function BecomePartner() {
  return (
    <div data-testid="become-partner-page">
      <SEOHead pageKey="become-partner" />
      <div className="bg-[#1A1A1A] py-16 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--brand-primary)] mb-3">For Service Providers &amp; Vendors</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mb-5 max-w-3xl">Partner with Gulfudaan and get a steady stream of work</h1>
          <p className="text-base text-white/60 max-w-2xl leading-relaxed mb-8">
            Visa stamping, attestation, document processing — if you deliver Gulf-related services, we assign you real jobs with transparent payouts. Registration is required to receive assignments.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/partner/register">
              <Button data-testid="become-partner-register-btn" className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white h-12 px-8 text-base font-bold">
                Register as a Partner <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/partner/login">
              <Button data-testid="become-partner-login-btn" variant="outline" className="border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-8 text-base">
                Already a partner? Log in
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <div className="bg-[#FAF8F4] py-14 sm:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading font-bold text-lg text-[#1A1A1A] mb-8">Why service providers partner with us</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="become-partner-benefits">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-lg border border-[#E5E1DA] bg-white p-6">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#b1976b]/15 mb-3">
                  <b.icon size={18} className="text-[var(--brand-primary)]" />
                </span>
                <p className="font-heading font-bold text-[15px] text-[#1A1A1A] mb-1.5">{b.title}</p>
                <p className="text-[13px] text-[#54595f] leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white py-14 sm:py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-lg text-[#1A1A1A] mb-6">How to become a partner</h2>
          <div className="space-y-4 mb-10" data-testid="become-partner-steps">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[var(--brand-primary)] mt-0.5 shrink-0" />
                <p className="text-sm text-[#54595f] leading-relaxed"><span className="font-bold text-[#1A1A1A]">Step {i + 1}:</span> {s}</p>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-[color-mix(in_oklab,var(--brand-primary),transparent_70%)] bg-[color-mix(in_oklab,var(--brand-primary),transparent_94%)] p-6 text-center">
            <p className="font-heading font-bold text-base text-[#1A1A1A] mb-2">Partner access requires registration</p>
            <p className="text-[13px] text-[#54595f] mb-4">Job assignments, payouts and the partner portal are only available to verified registered partners.</p>
            <Link to="/partner/register">
              <Button data-testid="become-partner-cta-bottom" className="bg-[var(--brand-primary)] hover:bg-[#9E855A] text-white h-11 px-8 font-bold">
                Start Registration Now <ArrowRight size={15} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
